import { Box, Card, Grid, Stack } from "@mui/material";
import { getProcessingRequestStatus, selectRouteToView, shouldUseMultistartComputationStatus } from "../trips/tripUtils";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RowActions from "./RowActions";
import TripCardHeader from "./TripCardHeader";
import { ComputationStatus, computationStatusFromJSON, Trip, Waypoint } from "../../generated/gpb/api/route";
import ThumbnailMap from "../risk/ThumbnailMap";
import ThumbnailTimer from "./ThumbnailTimer";
import { getTripDrawerWidth } from "./TripDrawer";
import { useIsMobile } from "../../features/pageWidth/pageWidthSlice";
import { useDispatch } from "react-redux";
import { setIsPlaying } from "../../features/time/playbackSlice";
import { closeDrawer } from "../../features/Drawer/drawerSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/sharp-duotone-light-svg-icons";
import useIdentity from "../../app/auth";
import React, { useEffect } from "react";
import { useUserViewOverride } from "../../features/riskViewer/userViewOverrideSlice";
import { useLazyGetTripStatusQuery } from "../../services/routing";

type TripCardProps = {
    trip: Trip,
    resetToPage: ( event: React.ChangeEvent<unknown>, value: number ) => void,
    refetch: () => void,
};

const subtextColor = "rgb(153, 153, 153)";
// Utility function to render a single waypoint label
function getWaypointLabelForCard(
    waypoint: Waypoint | undefined,
    totalWaypoints: number,
    index: number,
): JSX.Element
{
    if ( waypoint === undefined || waypoint.location === undefined )
    {
        return (
            <span style = {{ color: subtextColor }} key = {`waypointToShow${index}`}>
                <MoreVertIcon/>
                <span style = {{ position: "relative", bottom: "5px" }}>
                    {totalWaypoints - 2} more waypoints
                </span>
            </span>
        );
    }

    return (
        <span key = {`waypointToShow${index}`}>
            <FontAwesomeIcon
                icon = {faLocationDot}
                size = "lg"
                fixedWidth
                style = {{
                    color: subtextColor,
                    "--fa-secondary-color": "white",
                    //use format recommended by fontawesome
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any}
            />
            <span style = {{ color: subtextColor }}>{waypoint.location?.locationName}</span>
        </span>
    );
}

/**
 * Renders a list of waypoints, condensing the middle ones if needed.
 *
 * @param {Array<Waypoint> | undefined} waypoints - The full array of waypoints.
 * @returns {JSX.Element | undefined} The rendered list of waypoints.
 */
export function getWaypointList( waypoints: Array<Waypoint> | undefined ): JSX.Element | undefined
{
    if ( waypoints === undefined ) return;

    const waypointsToShow =
        waypoints.length > 2
            ? [ waypoints[0], undefined, waypoints[waypoints.length - 1] ]
            : waypoints;

    return (
        <Stack sx = {{ fontSize: "12px" }}>
            {waypointsToShow.map( ( waypoint, index ) => getWaypointLabelForCard(
                ( waypoint !== undefined && waypoint.location !== undefined ) ? waypoint : undefined,
                waypoints.length,
                index,
            ) )}
        </Stack>
    );
}

/**
 * TripCard component to display trip details.
 *
 * @param {TripCardProps} props - Props containing trip.
 * @returns {JSX.Element} The rendered TripCard component.
 */
export default function TripCard( { trip, refetch, resetToPage }: Readonly<TripCardProps> )
{
    const isMobile = useIsMobile();
    const userViewOverride = useUserViewOverride();
    const { isAdmin } = useIdentity( userViewOverride );
    const dispatch = useDispatch();
    const computationStatus = getProcessingRequestStatus( trip );
    const [ triggerGetTripStatus ] = useLazyGetTripStatusQuery();


    const startTimeMillis = shouldUseMultistartComputationStatus( trip?.routeState )
        ? trip?.processingRequestStatusMultistart?.lastReportedTime
        : trip?.processingRequestStatus?.lastReportedTime;

    const onMapClick = () =>
    {
        if ( computationStatus === ComputationStatus.IN_PROGRESS || computationStatus === ComputationStatus.WAITING )
        {
            return;
        }
        selectRouteToView( trip, dispatch, isAdmin );
        dispatch( setIsPlaying( false ) );
        if ( isMobile )
        {
            dispatch( closeDrawer() );
        }
    };

    useEffect( () =>
    {
        const checkTripStatus = setInterval( () =>
        {
            if ( computationStatus === ComputationStatus.IN_PROGRESS )
            {
                const tripIdString = trip.Id?.hexString;
                if ( tripIdString !== undefined )
                {
                    const processingRequestStatusPromise = triggerGetTripStatus( tripIdString ).unwrap();
                    void processingRequestStatusPromise.then( ( processingRequestStatus ) =>
                    {
                        if ( computationStatusFromJSON( processingRequestStatus.computationStatus ) !== ComputationStatus.IN_PROGRESS )
                        {
                            refetch();
                        }
                    } );
                }
            }
        }, 1000 );

        return () => clearInterval( checkTripStatus );
    }, [ trip.Id ] );

    return (
        <Card sx = {{ backgroundColor: "lightgrey", borderRadius: "0px", display: "flex", justifyContent: "center" }}>
            <Stack
                sx = {{
                    boxShadow: 3,
                    padding: 1,
                    margin: 1,
                    backgroundColor: "white",
                    borderRadius: "4px",
                    width: `calc(${getTripDrawerWidth( isMobile )} - 10%)`,
                    cursor: "pointer",
                }}
                onClick = {onMapClick}
            >
                <Box>
                    {( computationStatus === ComputationStatus.IN_PROGRESS || computationStatus === ComputationStatus.WAITING ) ? (
                        <ThumbnailTimer startTime = {startTimeMillis}/>
                    ) : (
                        <ThumbnailMap trip = {trip} resetToPage = {resetToPage}/>
                    )}
                </Box>
                <TripCardHeader trip = {trip}/>
                <Grid container spacing = {2}>
                    <Grid item xs = {10}> {/* Left column */}
                        {getWaypointList( trip?.routeRequestInput?.waypoints )}
                    </Grid>
                    <Grid item xs = {2}> {/* Right column */}
                        <Stack justifyContent = "space-between" height = "100%">
                            {/* Actions Bar at the bottom */}
                            <Stack direction = "row" sx = {{ mt: "auto", justifyContent: "flex-end" }}>
                                <RowActions trip = {trip}/>
                            </Stack>
                        </Stack>
                    </Grid>
                </Grid>
                {isAdmin && trip?.userId !== undefined &&
                    <span style = {{ color: subtextColor }}>This trip was generated by user {trip?.userId.wordpressId}</span>}
            </Stack>
        </Card>
    );
}


